import { memo, useCallback, type KeyboardEvent, type ReactNode } from 'react'

interface PageHeaderProps {
  title: string
  actions?: ReactNode
  brand?: string
}

export const PageHeader = memo(function PageHeader({
  title,
  actions,
  brand = 'TrafficPlay',
}: PageHeaderProps) {
  return (
    <header className="tp-page-header">
      <div className="tp-page-header__identity" aria-label={`${brand}, ${title}`}>
        <span className="tp-page-header__brand">{brand}</span>
        <span className="tp-page-header__separator" aria-hidden="true">›</span>
        <h1 className="tp-page-header__title">{title}</h1>
      </div>
      {actions && <div className="tp-page-header__actions">{actions}</div>}
    </header>
  )
})

export interface PageTab {
  id: string
  label: string
}

interface PageTabsProps {
  items: readonly PageTab[]
  activeId: string
  onChange: (id: string) => void
  ariaLabel: string
  controlsId: string
  idPrefix: string
}

export const PageTabs = memo(function PageTabs({
  items,
  activeId,
  onChange,
  ariaLabel,
  controlsId,
  idPrefix,
}: PageTabsProps) {
  const handleKeyDown = useCallback((event: KeyboardEvent<HTMLButtonElement>) => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return

    const currentIndex = items.findIndex(item => item.id === activeId)
    const nextIndex = event.key === 'Home'
      ? 0
      : event.key === 'End'
        ? items.length - 1
        : (currentIndex + (event.key === 'ArrowRight' ? 1 : -1) + items.length) % items.length

    event.preventDefault()
    onChange(items[nextIndex].id)
    const tabs = event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>('[role="tab"]')
    tabs?.[nextIndex]?.focus()
  }, [activeId, items, onChange])

  return (
    <nav className="tp-page-tabs" role="tablist" aria-label={ariaLabel}>
      {items.map(item => {
        const active = item.id === activeId
        return (
          <button
            key={item.id}
            id={`${idPrefix}-${item.id}`}
            type="button"
            className={`tp-page-tab${active ? ' is-active' : ''}`}
            role="tab"
            aria-selected={active}
            aria-controls={controlsId}
            tabIndex={active ? 0 : -1}
            onClick={() => onChange(item.id)}
            onKeyDown={handleKeyDown}
          >
            {item.label}
          </button>
        )
      })}
    </nav>
  )
})
